import type { ReactNode } from 'react';

import { PencilIcon } from 'lucide-react';

import { cn } from '@nesso/ui/lib/utils';
import { DialogCloseButton } from '@nesso/ui/primitives/dialog';
import {
    PopoverClose,
    PopoverContent,
    type PopoverContentProps,
} from '@nesso/ui/primitives/popover';

import { PopoverTooltipButton } from '@/shared/ui/common/popover-tooltip-button';

type Props = {
    buttonClassName: string;
    disabled: boolean;
    open: boolean;
    popoverContentProps: Omit<PopoverContentProps, 'children'>;
    onOpen: (open: boolean) => void;
    children: ReactNode;
};

export function EditTreatmentApproachesPopover({
    buttonClassName,
    disabled,
    open,
    popoverContentProps,
    onOpen,
    children,
}: Props) {
    const { className: popoverClassName, ...restPopoverContentProps } = popoverContentProps ?? {};
    const onClose = () => onOpen(false);
    return (
        <PopoverTooltipButton
            open={open}
            onOpenChange={onOpen}
            popoverProps={{ modal: true }}
            tooltipContent={<p>Edit treatment approach</p>}
            tooltipContentProps={{ showArrow: false }}
            popoverContent={
                <PopoverContent
                    className={cn('relative min-w-[375px] p-4 gap-4', popoverClassName)}
                    side="bottom"
                    align="start"
                    sideOffset={8}
                    {...restPopoverContentProps}
                >
                    <PopoverClose asChild>
                        <DialogCloseButton
                            className="absolute top-4 right-4 hover:bg-accent hover:text-muted-foreground"
                            onClick={onClose}
                        />
                    </PopoverClose>
                    {children}
                </PopoverContent>
            }
            wrapPopoverContent={false}
            buttonProps={{
                variant: 'ghost',
                size: 'icon',
                'aria-label': 'Edit treatment approach',
                className: buttonClassName,
                disabled,
            }}
        >
            <PencilIcon className="w-3 h-3" />
        </PopoverTooltipButton>
    );
}
